import React, { useState } from 'react';
import Image from 'components/shared/image';
import SiteLink from 'components/shared/sitelink';
import { formatDate, addDaysToDate, checkForReturn, addLocaleToHref } from 'lib/utils/helpers';
import { useAddItemsToCart } from 'hooks';
import { useRouter } from 'next/router';
import getShopifyData from 'lib/shopify/getShopifyData';
import { getCurrencySymbol } from 'lib/shopify';
import OrderDetails from './orderDetails';
import OutOfStockPopup from './outOfStockPopup';
import styles from './ordersSection.module.scss';

const OrdersSection = ({
  customer,
  showDetails,
  locale,
  setShowDetails,
  ordersaccountpage,
  orderdetailsaccountpage,
  secondorderdetailsaccountpage,
  order,
  setOrder,
}) => {
  const {
    ordersSummaryTitle,
    ordersReturnPossibleContent,
    ordersReturnNotPossibleContent,
    viewOrdersButtonContent,
    ordersReorderButtonContent,
    ordersReturnButtonContent,
    ordersEmptyMessageContent,
    ordersTrackButtonContent,
  } = ordersaccountpage;

  const addItems = useAddItemsToCart();
  const descendingOrders = customer.orders.edges.slice().reverse();
  const router = useRouter();
  const [outOfStockProducts, setOutOfStockProducts] = useState(null);
  const [showOutOfStockPopup, setShowOutOfStockPopup] = useState(false);

  const handleReorder = async (e, node) => {
    e.preventDefault();

    if (!node) return;

    const productIds = node.lineItems?.edges
      ?.map(item => item?.node?.variant?.product.id)
      .filter(Boolean);

    const actualShopifyData = await getShopifyData({ productIds, locale });
    const productsOutOfStock = actualShopifyData
      .filter(p => p.outOfStock)
      .map(p => {
        const lineItem = node.lineItems?.edges?.find(
          item => item?.node?.variant?.product.id === p.productId
        )?.node?.variant;

        return {
          ...p,
          ...lineItem,
        };
      });

    if (productsOutOfStock.length) {
      setOutOfStockProducts(productsOutOfStock);
      setShowOutOfStockPopup(true);
    } else {
      const itemsToBeAddedToCart = node.lineItems?.edges
        ?.filter(item => Boolean(item?.node?.variant?.id))
        ?.map(item => ({
          variantId: `${item?.node?.variant?.id}`,
          quantity: item?.node?.quantity,
        }));

      await addItems(itemsToBeAddedToCart);

      // not passing router to addLocaleToHref, because it's an account page
      router.push(addLocaleToHref('/cart/', locale));
    }
  };

  return (
    <>
      {showOutOfStockPopup && (
        <OutOfStockPopup
          locale={locale}
          closeModal={setShowOutOfStockPopup}
          outOfStockProducts={outOfStockProducts}
          globalModule={ordersaccountpage}
        />
      )}
      <h1 className={styles.title}>{secondorderdetailsaccountpage.ordersTitle?.text}</h1>
      {!showDetails ? (
        <ul className={styles.ordersList}>
          {customer.orders && customer.orders.edges.length >= 1 ? (
            descendingOrders.map(({ node }) => {
              const returnDate = addDaysToDate(node?.processedAt, 14);
              const formatReturnDate = formatDate(returnDate);
              const trackingUrl = node?.successfulFulfillments?.[0]?.trackingInfo?.[0]?.url;

              return (
                <li key={node.orderNumber}>
                  <div className={styles.order}>
                    <div className={styles.details}>
                      <h2 className={styles.ordersTitle}>
                        {ordersSummaryTitle?.text}: {node.orderNumber}
                      </h2>
                      <span className={styles.orderInfo}>
                        {formatDate(node?.processedAt)} | {getCurrencySymbol(locale)}
                        {node?.totalPrice?.amount} |{' '}
                        {checkForReturn(returnDate)
                          ? `${ordersReturnPossibleContent?.text} ${formatReturnDate}`
                          : `${ordersReturnNotPossibleContent?.text}`}
                      </span>
                      <br />
                      <ul className={styles.imageContainer}>
                        {node.lineItems.edges.map(({ node: lineItem }) => (
                          <li key={lineItem.title}>
                            <Image
                              src={lineItem?.variant?.image}
                              layout="fill"
                              className={styles.image}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className={styles.buttonContainer}>
                      {trackingUrl && (
                        <SiteLink external={trackingUrl}>
                          <button className={styles.orderButton} type="submit">
                            {ordersTrackButtonContent?.text}
                          </button>
                        </SiteLink>
                      )}
                      <button
                        className={styles.orderButton}
                        type="submit"
                        onClick={() => {
                          setOrder(node);
                          setShowDetails(true);
                        }}
                      >
                        {viewOrdersButtonContent?.text}
                      </button>
                      <button
                        className={styles.orderButton}
                        type="submit"
                        onClick={e => handleReorder(e, node)}
                      >
                        {ordersReorderButtonContent?.text}
                      </button>
                      {checkForReturn(returnDate) && (
                        <SiteLink
                          external="https://www.dpd.com/nl/nl/support/veelgestelde-vragen/veelgestelde_vragen/retourneren/hoe-kan-ik-een-pakket-retour-sturen/"
                          className={styles.loginButton}
                        >
                          <button className={styles.orderButton} type="submit">
                            {ordersReturnButtonContent?.text}
                          </button>
                        </SiteLink>
                      )}
                    </div>
                  </div>
                </li>
              );
            })
          ) : (
            <span className={styles.emptyOrders}>{ordersEmptyMessageContent?.text}</span>
          )}
        </ul>
      ) : (
        <OrderDetails
          customer={customer}
          order={order}
          orderdetailsaccountpage={orderdetailsaccountpage}
          secondorderdetailsaccountpage={secondorderdetailsaccountpage}
          ordersReturnNotPossibleContent={ordersReturnNotPossibleContent}
          setOrder={setOrder}
          setShowDetails={setShowDetails}
          locale={locale}
        />
      )}
    </>
  );
};

export default OrdersSection;
